import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  ListingModalTextField,
  LISTING_ID_HELPER,
  LISTING_ID_LABEL,
  LISTING_ID_PLACEHOLDER,
  LISTING_MODAL_HELPER_SX,
} from './listingModalUi';
import { LISTING_STATUS_LABELS } from './listingDisplayStatus';
import { searchPartnerListings } from './analyticsListingApi';

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;
const MAX_PANEL_HEIGHT = 232;

/**
 * Listing ID field with search-as-you-type over the partner's listing source.
 *
 * Typing an address and picking the exact listing is the point: the ID the feed
 * matches on is rarely something an admin has to hand, and a hand-typed address
 * can never resolve. Selecting a row commits the feed's own identifier, which is
 * what makes automatic status tracking possible.
 *
 * Lives here rather than in listingModalUi/ because it fetches — that barrel is
 * presentational primitives, and pulling the API client (and the store behind
 * it) into it would widen every consumer's dependency graph.
 *
 * Suggestions render inline rather than in a floating popper so the panel can
 * never be clipped by the dialog's scroll container.
 *
 * Degrades to a plain text field when no listing source is configured, so the
 * manual-entry path is always available.
 */
const ListingIdPicker = ({
  partner,
  value,
  onChange,
  onSelect,
  selectedListing = null,
  disabled = false,
  autoFocus = false,
  label = LISTING_ID_LABEL,
  helperText = LISTING_ID_HELPER,
}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // Starts dismissed so opening a modal on an already-linked listing stays
  // quiet; focusing the field is what opts into suggestions.
  const [dismissed, setDismissed] = useState(true);
  const blurTimerRef = useRef(null);

  const query = String(value || '').trim();
  const isCommittedSelection =
    Boolean(selectedListing) && selectedListing.listingRef === query;
  const panelOpen = !dismissed && !disabled && !unavailable && !isCommittedSelection;

  useEffect(
    () => () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!panelOpen) return undefined;

    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const res = await searchPartnerListings(partner, query, {
          signal: controller.signal,
        });
        if (cancelled) return;
        if (res?.available === false) {
          setUnavailable(true);
          setResults([]);
        } else {
          setResults(res?.listings || []);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearched(true);
          setHighlight(-1);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [panelOpen, partner, query]);

  const handleTextChange = (event) => {
    setDismissed(false);
    setSearched(false);
    onSelect?.(null);
    onChange?.(event.target.value);
  };

  const handleSelect = useCallback(
    (listing) => {
      if (!listing?.listingRef) return;
      setDismissed(true);
      setResults([]);
      setSearched(false);
      setHighlight(-1);
      onChange?.(listing.listingRef);
      onSelect?.(listing);
    },
    [onChange, onSelect]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (panelOpen) {
        event.stopPropagation();
        setDismissed(true);
      }
      return;
    }
    if (!panelOpen || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((prev) => (prev + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
    } else if (event.key === 'Enter' && highlight >= 0) {
      event.preventDefault();
      handleSelect(results[highlight]);
    }
  };

  const showPanel =
    panelOpen && (loading || results.length > 0 || (searched && query.length >= MIN_QUERY_LENGTH));

  return (
    <Stack spacing={0.75}>
      <ListingModalTextField
        label={label}
        placeholder={LISTING_ID_PLACEHOLDER}
        value={value}
        onChange={handleTextChange}
        onFocus={() => {
          if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
          setDismissed(false);
        }}
        onBlur={() => {
          // Deferred so a click on a suggestion lands before the panel closes.
          blurTimerRef.current = setTimeout(() => setDismissed(true), 150);
        }}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        inputProps={{
          // On the native input rather than the field root: TextField forwards
          // unknown props to the wrapping FormControl, where key handling would
          // only work by bubbling.
          onKeyDown: handleKeyDown,
          'aria-autocomplete': 'list',
          'aria-expanded': showPanel,
        }}
      />

      {showPanel ? (
        <Box
          role="listbox"
          aria-label="Matching listings"
          sx={{
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            overflow: 'hidden',
            maxHeight: MAX_PANEL_HEIGHT,
            overflowY: 'auto',
            bgcolor: '#fff',
          }}
        >
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1.25 }}>
              <CircularProgress size={14} thickness={5} />
              <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>
                Searching listings…
              </Typography>
            </Stack>
          ) : null}

          {!loading && results.length === 0 ? (
            <Typography
              sx={{ px: 1.5, py: 1.25, fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}
            >
              No matching listings. Check the address, or enter the listing ID by hand.
            </Typography>
          ) : null}

          {!loading
            ? results.map((listing, index) => (
                <Box
                  key={listing.listingRef}
                  role="option"
                  aria-selected={index === highlight}
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(listing)}
                  onMouseEnter={() => setHighlight(index)}
                  sx={{
                    px: 1.5,
                    py: 1.1,
                    cursor: 'pointer',
                    bgcolor: index === highlight ? 'rgba(0,0,0,0.04)' : 'transparent',
                    borderTop: index === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.84rem', fontWeight: 600, color: 'rgba(0,0,0,0.82)' }}
                    noWrap
                  >
                    {listing.address || listing.listingRef}
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: 'rgba(0,0,0,0.5)' }} noWrap>
                    ID {listing.listingRef}
                    {' · '}
                    {LISTING_STATUS_LABELS[listing.status] ||
                      listing.rawStatus ||
                      LISTING_STATUS_LABELS.unknown}
                  </Typography>
                </Box>
              ))
            : null}
        </Box>
      ) : null}

      {isCommittedSelection ? (
        <Typography sx={{ ...LISTING_MODAL_HELPER_SX, mt: 0 }}>
          Matched {selectedListing.address || selectedListing.listingRef}. Status will track
          your listing site automatically.
        </Typography>
      ) : (
        <Typography sx={{ ...LISTING_MODAL_HELPER_SX, mt: 0 }}>{helperText}</Typography>
      )}
    </Stack>
  );
};

export default ListingIdPicker;
